import Autocomplete from "@mui/material/Autocomplete";
import Collapse from "@mui/material/Collapse";
import FormHelperText from "@mui/material/FormHelperText";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useCallback } from "react";
import type {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
} from "react-hook-form";
import { useController } from "react-hook-form";
import { TransitionGroup } from "react-transition-group";
import { RemoveButton } from "~components/shared/Buttons/RemoveButton.tsx";
import { AutocompleteOption } from "~components/shared/Form/AutocompleteOption.tsx";
import { IconWithLabel } from "~components/shared/Icon/IconWithLabel.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import styles from "./ItemMultiSelector.module.scss";
import { CheckableSelectedItem } from "./SelectedItem.tsx";

export interface ItemItemMultiSelectorLabelProps {
  label: string;
  noOptions: string;
  error?: string;
  removeResource: string;
  remove: string;
  selectedItemsHeadline?: string;
  checkedError?: string;
  open: string;
  close: string;
  clear: string;
  loading: string;
}

interface ItemMultiSelectorSharedProps<T> {
  name: string;
  options: T[];
  isOptionEqualToValue: (option: T, value: T) => boolean;
  getOptionLabel: (option: T) => string;
  getOptionId: (option: T) => string;
  getOptionIcon?: (option: T) => React.ReactNode;
  getOptionInfo?: (option: T) => React.ReactNode;
  getOptionSecondary?: (option: T) => React.ReactNode;
  isOptionCheckable?: (option: T) => boolean;
  textFieldProps?: TextFieldProps;
}

export interface ItemMultiSelectorProps<T>
  extends ItemMultiSelectorSharedProps<T> {
  onChange: (items: T[]) => void;
  onChangeChecked?: (...event: unknown[]) => void;
  onBlur: (...event: unknown[]) => void;
  value: T[];
  error: boolean;
  checkedError: boolean;
  isOptionChecked?: (option: T) => boolean;
  color?: "primary" | "secondary";
}

export function ItemMultiSelector<T>({
  value,
  onChange,
  onBlur,
  name,
  options,
  labels,
  error,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionId,
  getOptionIcon,
  getOptionInfo,
  getOptionSecondary,
  isOptionChecked,
  isOptionCheckable,
  checkedError,
  onChangeChecked,
  color = "primary",
  textFieldProps,
}: ItemMultiSelectorProps<T> & {
  labels: ItemItemMultiSelectorLabelProps;
}) {
  return (
    <div>
      <Autocomplete
        className={styles.autocomplete}
        autoComplete
        openOnFocus
        clearText={labels.clear}
        closeText={labels.close}
        openText={labels.open}
        loadingText={labels.loading}
        multiple
        options={options}
        value={value}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel}
        filterSelectedOptions={true}
        renderValue={() => null}
        clearIcon={null}
        blurOnSelect={true}
        noOptionsText={labels.noOptions}
        renderInput={(params) => (
          <TextField
            name={name}
            {...params}
            {...textFieldProps}
            color={color}
            label={labels.label}
            error={error}
          />
        )}
        renderOption={(props, option: T) => (
          <AutocompleteOption
            {...props}
            key={getOptionId(option)}
            icon={getOptionIcon?.(option)}
            label={getOptionLabel(option)}
            secondary={getOptionSecondary?.(option)}
            data-testid={`unselected-${getOptionId(option)}`}
          />
        )}
        onChange={(_e, items, reason) => {
          if (reason === "selectOption" && items) {
            onChange(items);
          }
        }}
        onBlur={onBlur}
      />
      <TransitionGroup>
        <Collapse>
          {value.length > 0 && labels.selectedItemsHeadline && (
            <Typography
              variant={"caption"}
              className={styles.selectedItemsHeadline}
            >
              {labels.selectedItemsHeadline}
            </Typography>
          )}
        </Collapse>
        {value?.map((option: T) => (
          <Collapse key={getOptionId(option)}>
            <CheckableSelectedItem
              variant="default"
              info={getOptionInfo?.(option)}
              color={color}
              id={getOptionId(option)}
              checked={isOptionChecked?.(option)}
              onChangeChecked={
                onChangeChecked &&
                isOptionChecked &&
                isOptionCheckable?.(option)
                  ? () => {
                      if (onChangeChecked && isOptionChecked) {
                        onChangeChecked(
                          isOptionChecked(option) ? null : option,
                        );
                      }
                    }
                  : undefined
              }
              actions=<RemoveButton
                tooltipResourceName={getOptionLabel(option)}
                onClick={() => {
                  if (
                    isOptionChecked &&
                    onChangeChecked &&
                    isOptionChecked(option)
                  ) {
                    onChangeChecked(null);
                  }
                  onChange(
                    value.filter((i) => !isOptionEqualToValue(i, option)),
                  );
                }}
              />
              data-testid={`selected-${getOptionId(option)}`}
            >
              <span className={styles.selectedItemContent}>
                <IconWithLabel icon={getOptionIcon?.(option)}>
                  {getOptionLabel(option)}
                </IconWithLabel>
                {getOptionSecondary?.(option) && (
                  <span className={styles.selectedItemSecondary}>
                    {getOptionSecondary(option)}
                  </span>
                )}
              </span>
            </CheckableSelectedItem>
          </Collapse>
        ))}
        <Collapse>
          {error && <FormHelperText error>{labels.error}</FormHelperText>}
          {checkedError && labels.checkedError && (
            <FormHelperText error>{labels.checkedError}</FormHelperText>
          )}
        </Collapse>
      </TransitionGroup>
    </div>
  );
}

type RequireSome<T, K extends keyof T> = {
  [P in K]-?: T[P];
} & {
  [P in Exclude<keyof T, K>]?: T[P];
};

interface ControlledItemMultiSelectorProps<
  F extends FieldValues,
  P extends Path<F>,
  I,
> extends ItemMultiSelectorSharedProps<I> {
  control: Control<F>;
  name: P;
  checkableFieldName: Path<F>;
  checkableRules?: UseControllerProps<F, Path<F>>["rules"];
  rules?: UseControllerProps<F, P>["rules"];
  labels: RequireSome<ItemItemMultiSelectorLabelProps, "label">;
  color?: "primary" | "secondary";
  beforeOnChange?: (value: I[], proceed: () => void) => void;
}

export function ControlledItemMultiSelector<
  F extends FieldValues = FieldValues,
  P extends Path<F> = Path<F>,
  I = PathValue<F, P> extends Array<infer Item> ? Item : never,
>({
  control,
  name,
  rules,
  checkableFieldName,
  checkableRules,
  isOptionEqualToValue,
  labels,
  color = "primary",
  beforeOnChange,
  ...props
}: ControlledItemMultiSelectorProps<F, P, I>) {
  const t = useTranslation();
  const {
    field: { onChange, onBlur, value: fieldValue },
    fieldState: { error },
  } = useController({ control, name, rules });

  const onCustomChange = useCallback(
    (items: I[]) => {
      if (beforeOnChange) {
        beforeOnChange(items, () => onChange(items));
      } else {
        onChange(items);
      }
    },
    [beforeOnChange, onChange],
  );

  const {
    field: { onChange: onChangeChecked, value: checkedValue },
    fieldState: { error: checkedError },
  } = useController({
    control,
    name: checkableFieldName,
    rules: checkableRules,
  });
  const {
    label,
    noOptions = t.noOptions,
    error: errorLabel = error?.message,
    removeResource = t.removeResource,
    remove = t.remove,
    selectedItemsHeadline,
    checkedError: checkedErrorLabel = checkedError?.message,
    open = t.open,
    close = t.close,
    clear = t.clear,
    loading = t.loading,
  } = labels;

  const isOptionChecked = (option: I) =>
    checkedValue && isOptionEqualToValue(option, checkedValue);

  return (
    <ItemMultiSelector<I>
      onChange={onCustomChange}
      onBlur={onBlur}
      value={fieldValue}
      isOptionEqualToValue={isOptionEqualToValue}
      onChangeChecked={onChangeChecked}
      isOptionChecked={isOptionChecked}
      name={name}
      error={!!error}
      checkedError={!!checkedError}
      labels={{
        label,
        noOptions,
        error: errorLabel,
        removeResource,
        remove,
        selectedItemsHeadline,
        checkedError: checkedErrorLabel,
        open,
        close,
        clear,
        loading,
      }}
      color={color}
      {...props}
    />
  );
}
